import {
  Fragment,
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from 'react';
import {
  Bug,
  Building2,
  CalendarDays,
  ChevronDown,
  ChevronRight,
  Cog,
  CreditCard,
  Flag,
  LayoutGrid,
  Link2,
  Medal,
  MessageSquare,
  ShieldCheck,
  Swords,
  TrendingUp,
  Trophy,
  UserCheck,
  Users,
  Wallet,
  type LucideIcon,
} from 'lucide-react';
import {
  createRole,
  deleteRole,
  fetchMatrix,
  isRoleAssignable,
  updateRole,
  updateRoleCapabilities,
  type CapabilityInfo,
  type MatrixResult,
  type RoleView,
  updateRoleAvailability,
} from '../../lib/api/platformRoles';
import { fetchOrganizations, type OrganizationSummary } from '../../lib/api/platformOrganizations';
import { AreaSection, AreaViewLink, RoleAreaOverview } from './RoleAreaOverview';

/** The one-page overview shows each role's full grid with nothing hidden. */
const NOTHING_HIDDEN: Set<string> = new Set();
import { OrganizationScopePicker } from './OrganizationScopePicker';

// ─────────────────────────────────────────────────────────────────────
// /sa → Rettigheter. THE PERMISSION MATRIX: roles down the side,
// capabilities across the top, a checkbox per cell.
//
// What a role holds is data (docs/architecture/AUTHORIZATION.md § Roles as
// data). Ticking a box and saving the row changes the answer every request
// on the platform gets from then on — no deployment. That is the feature,
// and also the reason the page is careful: a row is saved as a whole and
// only on an explicit «Lagre», the server's refusals are rendered verbatim,
// and identity-derived capabilities are not offered as columns at all
// (the server would refuse them; better that the page cannot ask).
//
// `canWrite` mirrors the server's split so the page does not offer controls
// the API would refuse. It is NOT the control.
// ─────────────────────────────────────────────────────────────────────

export interface PermissionMatrixSectionProps {
  /** Does the viewer hold `platform.roles.manage`, per the SERVER's answer? */
  canWrite: boolean;
  /**
   * Which view the organization tabs open on. The overview by default
   * (decisions 1A–3A, 2026-10-19); the grid's own characterization tests open
   * on `'detail'`, which is the grid exactly as it was.
   */
  initialView?: 'overview' | 'detail';
  /** Injectable for tests. */
  load?: typeof fetchMatrix;
  save?: typeof updateRoleCapabilities;
  create?: typeof createRole;
  remove?: typeof deleteRole;
  rename?: typeof updateRole;
  setAvailability?: typeof updateRoleAvailability;
  loadOrganizations?: typeof fetchOrganizations;
}

/**
 * THE FOUR TABS. One board at a time, and the organization scope is split in
 * two — a centre and a club are different businesses (owner, 2026-10-15:
 * «vi må også skille på senter og klubb»), and a page that stacked three
 * boards and filtered one of them was read as one long, undifferentiated
 * list. Senter first, because that is where the courts and the money are.
 */
type Tab = 'CENTRE' | 'CLUB' | 'PLATFORM' | 'LINK';
const TABS: { tab: Tab; scope: RoleView['scope']; label: string; blurb: string }[] = [
  {
    tab: 'CENTRE',
    scope: 'ORGANIZATION',
    label: 'Senter',
    blurb: 'Roller hos et senter. Senteret eier banene, åpningstidene og bookingreglene.',
  },
  {
    tab: 'CLUB',
    scope: 'ORGANIZATION',
    label: 'Klubb',
    blurb: 'Roller i en klubb. Klubben eier aktivitetene, kampene og resultatene.',
  },
  {
    tab: 'PLATFORM',
    scope: 'PLATFORM',
    label: 'Starpoint',
    blurb: 'Starpoints egne folk — dette panelet, på tvers av alle organisasjoner.',
  },
  {
    tab: 'LINK',
    scope: 'LINK',
    label: 'Samarbeid',
    blurb: 'Hva et senter og en klubb får gjøre hos hverandre, via en kobling.',
  },
];

/**
 * WHICH SIDE A CAPABILITY BELONGS TO, for the two organization tabs.
 *
 * Not a server rule — the server accepts any organization capability in any
 * organization role, and a shared role may hold rights from both sides. It
 * is the product's reading (docs/domains/ROLES_AND_ACCESS.md § 4): courts,
 * opening hours and booking rules are the centre's; matches, results and
 * rating are the club's; the rest is both. The other side's rights are not
 * removed from a tab, only folded at the bottom of it.
 */
type Side = 'CENTRE' | 'CLUB' | 'BOTH';
const DOMAIN_SIDE: Record<string, Side> = {
  court: 'CENTRE',
  planned_match: 'CLUB',
  result: 'CLUB',
  rating: 'CLUB',
};
const CAPABILITY_SIDE: Record<string, Side> = {
  'booking.rules.manage': 'CENTRE',
  'booking.override_constraints': 'CENTRE',
};
function sideOf(c: CapabilityInfo): Side {
  return CAPABILITY_SIDE[c.key] ?? DOMAIN_SIDE[c.key.split('.')[0]] ?? 'BOTH';
}
const SIDE_LABEL: Record<Side, string> = {
  CENTRE: 'Senterets',
  CLUB: 'Klubbens',
  BOTH: 'Felles',
};

/**
 * A CATEGORY'S COLOUR, as literal class strings — Tailwind only emits what it
 * can read, so these are spelled out rather than built from a hue name.
 */
interface Tone {
  head: string;
  icon: string;
  stripe: string;
}
const TONES = {
  violet: {
    head: 'bg-violet-50 text-violet-950',
    icon: 'bg-violet-100 text-violet-700',
    stripe: 'border-l-violet-400',
  },
  sky: {
    head: 'bg-sky-50 text-sky-950',
    icon: 'bg-sky-100 text-sky-700',
    stripe: 'border-l-sky-400',
  },
  emerald: {
    head: 'bg-emerald-50 text-emerald-950',
    icon: 'bg-emerald-100 text-emerald-700',
    stripe: 'border-l-emerald-400',
  },
  orange: {
    head: 'bg-orange-50 text-orange-950',
    icon: 'bg-orange-100 text-orange-700',
    stripe: 'border-l-orange-400',
  },
  amber: {
    head: 'bg-amber-50 text-amber-950',
    icon: 'bg-amber-100 text-amber-700',
    stripe: 'border-l-amber-400',
  },
  yellow: {
    head: 'bg-yellow-50 text-yellow-950',
    icon: 'bg-yellow-100 text-yellow-700',
    stripe: 'border-l-yellow-400',
  },
  fuchsia: {
    head: 'bg-fuchsia-50 text-fuchsia-950',
    icon: 'bg-fuchsia-100 text-fuchsia-700',
    stripe: 'border-l-fuchsia-400',
  },
  teal: {
    head: 'bg-teal-50 text-teal-950',
    icon: 'bg-teal-100 text-teal-700',
    stripe: 'border-l-teal-400',
  },
  cyan: {
    head: 'bg-cyan-50 text-cyan-950',
    icon: 'bg-cyan-100 text-cyan-700',
    stripe: 'border-l-cyan-400',
  },
  indigo: {
    head: 'bg-indigo-50 text-indigo-950',
    icon: 'bg-indigo-100 text-indigo-700',
    stripe: 'border-l-indigo-400',
  },
  rose: {
    head: 'bg-rose-50 text-rose-950',
    icon: 'bg-rose-100 text-rose-700',
    stripe: 'border-l-rose-400',
  },
  slate: {
    head: 'bg-slate-100 text-slate-900',
    icon: 'bg-slate-200 text-slate-700',
    stripe: 'border-l-slate-400',
  },
} satisfies Record<string, Tone>;

/**
 * THE CATEGORIES — one per domain prefix, each with a colour, an icon and a
 * line saying what it covers. A domain this map does not know still gets a
 * heading (its prefix, in slate) so a new capability is never invisible.
 */
interface Category {
  label: string;
  blurb: string;
  Icon: LucideIcon;
  tone: Tone;
}
const CATEGORY: Record<string, Category> = {
  organization: {
    label: 'Organisasjonen',
    blurb: 'Lese, endre og navngi senteret eller klubben.',
    Icon: Building2,
    tone: TONES.violet,
  },
  court: {
    label: 'Baner og anlegg',
    blurb: 'Banene, åpningstidene og Banepad-nettbrettene.',
    Icon: LayoutGrid,
    tone: TONES.sky,
  },
  booking: {
    label: 'Booking',
    blurb: 'Bookinger på vegne av andre, og reglene og prisene bak dem.',
    Icon: CalendarDays,
    tone: TONES.emerald,
  },
  activity: {
    label: 'Aktiviteter',
    blurb: 'Treninger og andre aktiviteter, og påmeldingene til dem.',
    Icon: Flag,
    tone: TONES.orange,
  },
  tournament: {
    label: 'Turneringer og serier',
    blurb: 'Opprette, starte, pause og arkivere, og oppsettene bak dem.',
    Icon: Medal,
    tone: TONES.orange,
  },
  planned_match: {
    label: 'Planlagte kamper',
    blurb: 'Kamper som skal spilles, satt opp før de er spilt.',
    Icon: Swords,
    tone: TONES.amber,
  },
  result: {
    label: 'Resultater',
    blurb: 'Føre et resultat, og rette et som allerede er ført.',
    Icon: Trophy,
    tone: TONES.yellow,
  },
  rating: {
    label: 'Rating',
    blurb: 'Hva som får slå inn på spillernes rating.',
    Icon: TrendingUp,
    tone: TONES.fuchsia,
  },
  member: {
    label: 'Medlemmer',
    blurb: 'Hvem som står på medlemslisten.',
    Icon: Users,
    tone: TONES.teal,
  },
  membership: {
    label: 'Medlemskap',
    blurb: 'Søknader om medlemskap, medlemspris og medlemstilbud.',
    Icon: UserCheck,
    tone: TONES.cyan,
  },
  messaging: {
    label: 'Meldinger',
    blurb: 'Svare som organisasjonen, sende til alle, moderere.',
    Icon: MessageSquare,
    tone: TONES.indigo,
  },
  bug: {
    label: 'Feilmeldinger',
    blurb: 'Feil sendt inn fra appen.',
    Icon: Bug,
    tone: TONES.rose,
  },
  finance: {
    label: 'Økonomi',
    blurb: 'Se omsetning, oppgjør og rapporter — uten å røre betalingene.',
    Icon: Wallet,
    tone: TONES.emerald,
  },
  payment: {
    label: 'Betaling',
    blurb: 'Refusjon og manuelle grep hos organisasjonen; avstemming er Starpoints.',
    Icon: CreditCard,
    tone: TONES.slate,
  },
  platform: {
    label: 'Plattformen',
    blurb: 'Dette panelet: matrisen, brukerne, kontoene.',
    Icon: ShieldCheck,
    tone: TONES.violet,
  },
  engine: {
    label: 'Motoren',
    blurb: 'Innstillinger som gjelder hele plattformen.',
    Icon: Cog,
    tone: TONES.slate,
  },
  link: {
    label: 'Hos den andre organisasjonen',
    blurb: 'Det en kobling gir den ene parten lov til hos den andre.',
    Icon: Link2,
    tone: TONES.teal,
  },
};
function categoryOf(domain: string): Category {
  return (
    CATEGORY[domain] ?? { label: domain, blurb: '', Icon: LayoutGrid, tone: TONES.slate as Tone }
  );
}

/**
 * A SHORT NAME PER RIGHT, so a row can be scanned and not only read. The
 * catalog's sentence stays under it, and the key stays as the tooltip. A
 * right this map does not know shows its sentence as the title, as before.
 */
const CAPABILITY_TITLE: Record<string, string> = {
  'organization.read': 'Se data',
  'organization.write': 'Endre oppsett og data',
  'organization.rename': 'Endre navn',
  'organization.access.manage': 'Dele ut roller',
  'organization.audit.read': 'Lese organisasjonens logg',
  'organization.links.manage': 'Inngå samarbeid',
  'court.manage': 'Opprette og endre baner',
  'asset.manage': 'Opprette og endre utstyr',
  'court.set_out_of_service': 'Sette bane ute av drift',
  'court.override_out_of_service': 'Booke på stengt bane',
  'court.pair_device': 'Koble til Banepad',
  'court.screen.control': 'Styre skjermene',
  'booking.create': 'Booke for andre',
  'booking.modify': 'Endre andres booking',
  'booking.delete': 'Avbestille andres booking',
  'booking.override_constraints': 'Booke utenom reglene',
  'booking.rules.manage': 'Bookingregler og priser',
  'activity.manage': 'Drifte aktiviteter',
  'planned_match.manage': 'Sette opp kamper',
  'tournament.manage': 'Drifte turneringer og serier',
  'result.record': 'Føre resultat',
  'result.correct': 'Rette ført resultat',
  'rating.commit': 'Slå resultat inn i rating',
  'rating.adjust_manual': 'Sette rating manuelt',
  'member.manage': 'Administrere medlemmer',
  'member.contact.read': 'Se kontaktinfo',
  'member.export': 'Eksportere medlemslisten',
  'member.delete': 'Slette spiller på hele plattformen',
  'membership.approve': 'Godkjenne medlemskap',
  'membership.pricing.manage': 'Medlemspris og medlemstilbud',
  'messaging.send_as_organization': 'Svare som organisasjonen',
  'messaging.broadcast': 'Sende til alle medlemmer',
  'messaging.moderate': 'Moderere meldinger',
  'messaging.disable_organization': 'Slå av meldinger',
  'bug.manage': 'Behandle feilmeldinger',
  'finance.read': 'Se økonomi og rapporter',
  'payment.refund': 'Refundere',
  'payment.manual_action': 'Gripe inn i en betaling',
  'payment.reconcile': 'Avstemme betalinger',
  'engine.settings.manage': 'Motorens innstillinger',
  'platform.roles.manage': 'Endre rettighetsmatrisen',
  'platform.directory.read': 'Se plattformbrukerne',
  'platform.accounts.manage': 'Administrere kontoer',
  'platform.organizations.manage': 'Sette opp organisasjoner',
  'platform.impersonate_organization': 'Jobbe som en organisasjon',
  'link.booking.create': 'Booke hos senteret',
  'link.booking.extended_horizon': 'Booke lenger frem',
  'link.booking.partner_rate': 'Booke til partnerpris',
  'link.activity.manage': 'Drifte aktiviteter hos den andre',
};

/**
 * WHICH CAPABILITIES A ROLE OF THIS SCOPE MAY HOLD.
 *
 * Mirrors `validateCapabilitiesForRole` on the server, which refuses an
 * organization capability in a PLATFORM role and vice versa, and (since
 * 2026-10-09) admits only the `link` class in a LINK role — what one
 * organization may do inside another.
 */
const SCOPE_DERIVATION: Record<string, string> = {
  ORGANIZATION: 'organization',
  PLATFORM: 'platform',
  LINK: 'link',
};

/**
 * THE ORDER THE GROUPS ARE DRAWN IN — the product's order, not the catalog's.
 * The catalog lists capabilities in the order they were added, which put a
 * court right after the booking rights and drew «Baner og anlegg» twice. A
 * domain this list does not know is appended where it first appears.
 *
 * EVERY CATALOG DOMAIN IS HERE (#861). `tournament` and `finance` were not, so
 * they fell through to «appended» and were drawn after everything else — the
 * tournaments below the bug reports, the money apart from the payments.
 * `permission-matrix-section.test.tsx` reads the catalog
 * (`apps/api/src/auth/capabilities.ts`) and fails on a domain missing here.
 */
export const DOMAIN_ORDER = [
  'organization',
  'court',
  // Equipment beside the courts (#912) — venue setup, read with the courts.
  'asset',
  'booking',
  'activity',
  'tournament',
  'planned_match',
  'result',
  'rating',
  'member',
  'membership',
  'messaging',
  'bug',
  'finance',
  'payment',
  'platform',
  'engine',
  'link',
];

/** Group capabilities by domain, merged across the list and in product order. */
function groupByDomain(rows: CapabilityInfo[]): { domain: string; caps: CapabilityInfo[] }[] {
  const byDomain = new Map<string, CapabilityInfo[]>();
  for (const c of rows) {
    const domain = c.key.split('.')[0];
    const list = byDomain.get(domain);
    if (list) list.push(c);
    else byDomain.set(domain, [c]);
  }
  const known = DOMAIN_ORDER.filter((d) => byDomain.has(d));
  const unknown = [...byDomain.keys()].filter((d) => !DOMAIN_ORDER.includes(d));
  return [...known, ...unknown].map((domain) => ({ domain, caps: byDomain.get(domain)! }));
}

/**
 * WHICH ROLES A TAB SHOWS. The two organization tabs split the organization
 * scope by who a role is for; a role with `appliesTo: null` is for both and
 * appears on both, marked so a tick on it is known to reach both sides.
 */
function rolesForTab(roles: RoleView[], tab: Tab): RoleView[] {
  const scope = TABS.find((t) => t.tab === tab)!.scope;
  return roles.filter(
    (r) =>
      r.scope === scope && (scope !== 'ORGANIZATION' || r.appliesTo == null || r.appliesTo === tab),
  );
}

const REFUSAL_TEXT: Record<string, string> = {
  identity_capability:
    'Denne rettigheten følger av hvem man er (f.eks. å lese en samtale man deltar i) og kan ikke gis til en rolle.',
  unknown_capability: 'Ukjent rettighet.',
  system_role: 'Systemroller kan ikke slettes.',
  role_in_use: 'Rollen er i bruk og kan ikke slettes før ingen har den.',
  key_taken: 'Nøkkelen er allerede i bruk.',
  invalid_key: 'Nøkkelen må være STORE BOKSTAVER, tall og understrek, f.eks. SENTER_RESEPSJON.',
  name_required: 'Rollen må ha et navn.',
};

/** The availability write's own refusals — each names a rule, not a field. */
const AVAILABILITY_REFUSAL: Record<string, string> = {
  system_role_availability:
    'Systemroller er tilgjengelige overalt. En systemrolle begrenset til noen klubber ville latt de andre stå uten den — og seeden ville satt den tilbake ved neste utrulling.',
  availability_requires_organization_scope:
    'Bare organisasjonsroller kan begrenses til organisasjoner. En plattformrolle holdes av Starpoints egne folk og har ingen organisasjon å begrenses til.',
  organizations_with_platform_availability:
    'Rollen er satt til hele plattformen, og da kan den ikke samtidig være begrenset til enkelte organisasjoner.',
  unknown_organization: 'Én eller flere av organisasjonene finnes ikke lenger. Last siden på nytt.',
  organization_kind_mismatch:
    'Rollen gjelder bare den ene typen organisasjon, så den kan ikke tilbys den andre.',
};

/** A capability's short column header: the part after the domain. */
export function PermissionMatrixSection({
  canWrite,
  initialView = 'overview',
  load = fetchMatrix,
  save = updateRoleCapabilities,
  create = createRole,
  remove = deleteRole,
  rename = updateRole,
  setAvailability = updateRoleAvailability,
  loadOrganizations = fetchOrganizations,
}: PermissionMatrixSectionProps) {
  const [result, setResult] = useState<MatrixResult | null>(null);
  /** Unsaved edits, per role id. Absent = row is as the server has it. */
  const [drafts, setDrafts] = useState<Record<string, string[]>>({});
  const [busyRole, setBusyRole] = useState<string | null>(null);
  /**
   * Which role's NAME is being edited, if any.
   *
   * Separate from `drafts`, which holds capability edits. The two are
   * different writes on the server — the matrix replaces a whole capability
   * set and records a diff, the rename touches only the human half — and
   * collapsing them here would mean one Save doing two things and one audit
   * entry answering neither question well.
   */
  const [editing, setEditing] = useState<{ id: string; name: string; description: string } | null>(
    null,
  );
  /**
   * Roles the operator has hidden, by id.
   *
   * HIDDEN, NOT DESELECTED — absence means shown, so a role created later
   * appears by itself. The opposite default would quietly hide every new role
   * from whoever was last looking at this page.
   */
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [notice, setNotice] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  /**
   * Which role's AVAILABILITY is being edited, and the last refusal about it.
   *
   * A third write beside the matrix row and the rename, kept separate for the
   * same reason they are: the server records each as its own audit event, and
   * one Save doing all three would leave a trail that answers none of the three
   * questions well.
   *
   * The notice is held per-role rather than in the page-wide `notice` because
   * the refusal that matters here names organizations, and it belongs beside
   * the chips it is about.
   */
  const [scopeRole, setScopeRole] = useState<string | null>(null);
  const [scopeNotice, setScopeNotice] = useState<string | null>(null);
  const [organizations, setOrganizations] = useState<OrganizationSummary[]>([]);
  const [tab, setTab] = useState<Tab>('CENTRE');
  // THE OVERVIEW FIRST (owner decisions 1A–3A, 2026-10-19): roles by area,
  // the full grid one click away. A draft made in either view is the same
  // draft, so switching views never loses an edit.
  const [view, setView] = useState<'overview' | 'detail'>(initialView);

  const reload = useCallback(async () => {
    setNotice(null);
    setResult(await load());
    setDrafts({});
  }, [load]);

  useEffect(() => {
    void reload();
  }, [reload]);

  // FETCHED ONLY WHEN A PICKER IS OPENED, and once. The matrix is useful without
  // the organization list — most roles are platform-wide — so paying for the
  // list on every visit to «Rettigheter» would be a request nobody asked for.
  useEffect(() => {
    // Fetched when a picker is opened OR when the create form is, since that
    // form now offers the same choice — the list is the same either way.
    if ((scopeRole === null && !creating) || organizations.length > 0) return;
    void (async () => {
      const res = await loadOrganizations();
      if (res.state === 'ok') setOrganizations(res.organizations);
      else setScopeNotice('Kunne ikke hente organisasjonene.');
    })();
  }, [scopeRole, creating, organizations.length, loadOrganizations]);

  const columns: CapabilityInfo[] = useMemo(
    () => (result?.state === 'ok' ? result.capabilities.filter(isRoleAssignable) : []),
    [result],
  );
  // Does any link right do anything yet? Read from the server's `inForce`
  // rather than written into the «Ny rolle» help, so the sentence stops
  // saying «not yet» on the day a route starts asking (#861).
  const linkDormant = columns
    .filter((c) => c.derivation === 'link')
    .every((c) => c.inForce === false);

  const rowValue = (role: RoleView) => drafts[role.id] ?? role.capabilities;
  const isDirty = (role: RoleView) =>
    drafts[role.id] !== undefined &&
    drafts[role.id].slice().sort().join(',') !== role.capabilities.slice().sort().join(',');

  /**
   * WHAT A SAVE WOULD CHANGE, as two lists.
   *
   * Computed against what the SERVER last said the role holds, not against
   * the previous draft — the question being answered is «what will this
   * button do», and that is always measured from the stored row.
   */
  const pendingChange = (role: RoleView) => {
    const next = new Set(rowValue(role));
    const before = new Set(role.capabilities);
    return {
      added: [...next].filter((c) => !before.has(c)).sort(),
      removed: [...before].filter((c) => !next.has(c)).sort(),
    };
  };

  const toggle = (role: RoleView, capability: string) => {
    const current = rowValue(role);
    const next = current.includes(capability)
      ? current.filter((c) => c !== capability)
      : [...current, capability];
    setDrafts((d) => ({ ...d, [role.id]: next }));
  };

  const saveRow = async (role: RoleView) => {
    setBusyRole(role.id);
    setNotice(null);
    try {
      const res = await save(role.id, rowValue(role));
      if (res.state === 'ok') {
        setResult((prev) =>
          prev && prev.state === 'ok'
            ? { ...prev, roles: prev.roles.map((r) => (r.id === role.id ? res.role : r)) }
            : prev,
        );
        setDrafts((d) => {
          const { [role.id]: _dropped, ...rest } = d;
          void _dropped;
          return rest;
        });
      } else if (res.state === 'refused') {
        setNotice(REFUSAL_TEXT[res.reason] ?? `Serveren avslo endringen (${res.reason}).`);
      } else if (res.state === 'forbidden') {
        setNotice('Serveren avslo endringen.');
      } else {
        setNotice('Kunne ikke lagre.');
      }
    } finally {
      setBusyRole(null);
    }
  };

  const saveAvailability = async (
    role: RoleView,
    input: { availability: 'PLATFORM' | 'SELECTED'; organizationIds: string[] },
  ) => {
    setBusyRole(role.id);
    setScopeNotice(null);
    try {
      const res = await setAvailability(role.id, input);
      if (res.state === 'ok') {
        setResult((prev) =>
          prev && prev.state === 'ok'
            ? { ...prev, roles: prev.roles.map((r) => (r.id === res.role.id ? res.role : r)) }
            : prev,
        );
        setScopeRole(null);
      } else if (res.state === 'in-use') {
        // THE SERVER'S OWN SENTENCE, plus the organizations it named. «Rollen er
        // i bruk» without saying WHERE leaves the operator opening clubs one at
        // a time to find out.
        const names = res.organizations.map((o) => o.name).join(', ');
        setScopeNotice(names ? `${res.message} (${names})` : res.message);
      } else if (res.state === 'refused') {
        setScopeNotice(
          AVAILABILITY_REFUSAL[res.reason] ?? `Serveren avslo endringen (${res.reason}).`,
        );
      } else if (res.state === 'forbidden') {
        setScopeNotice('Serveren avslo endringen.');
      } else {
        setScopeNotice('Kunne ikke lagre tilgjengeligheten.');
      }
    } finally {
      setBusyRole(null);
    }
  };

  const renameRole = async () => {
    if (!editing) return;
    const name = editing.name.trim();
    if (!name) return;
    setBusyRole(editing.id);
    setNotice(null);
    try {
      // The description is sent even when empty: «cleared» is a change the
      // server understands (it stores null), and omitting it would make
      // emptying a description impossible.
      const res = await rename(editing.id, { name, description: editing.description.trim() });
      if (res.state === 'ok') {
        setResult((prev) =>
          prev && prev.state === 'ok'
            ? { ...prev, roles: prev.roles.map((r) => (r.id === res.role.id ? res.role : r)) }
            : prev,
        );
        setEditing(null);
      } else if (res.state === 'refused') {
        setNotice(REFUSAL_TEXT[res.reason] ?? `Serveren avslo endringen (${res.reason}).`);
      } else if (res.state === 'forbidden') {
        setNotice('Serveren avslo endringen.');
      } else {
        setNotice('Kunne ikke lagre navnet.');
      }
    } finally {
      setBusyRole(null);
    }
  };

  const removeRow = async (role: RoleView) => {
    setBusyRole(role.id);
    setNotice(null);
    try {
      const res = await remove(role.id);
      if (res.state === 'ok') {
        setResult((prev) =>
          prev && prev.state === 'ok'
            ? { ...prev, roles: prev.roles.filter((r) => r.id !== role.id) }
            : prev,
        );
      } else if (res.state === 'refused') {
        setNotice(REFUSAL_TEXT[res.reason] ?? `Serveren avslo slettingen (${res.reason}).`);
      } else {
        setNotice('Kunne ikke slette rollen.');
      }
    } finally {
      setBusyRole(null);
    }
  };

  return (
    <section className="card p-6" data-testid="sa-matrix">
      <div className="flex items-baseline justify-between gap-3 flex-wrap">
        <div>
          <h2 className="font-display text-sm font-semibold">Rettigheter</h2>
          <p className="text-[11px] text-ink-3 mt-1">
            Hva hver rolle får lov til. En endring gjelder fra neste forespørsel — ingen utrulling.
            Lagre én rad om gangen.
          </p>
        </div>
        {canWrite && result?.state === 'ok' && (
          <button
            type="button"
            className="btn-ghost text-xs"
            onClick={() => setCreating((v) => !v)}
            data-testid="sa-matrix-new"
          >
            {creating ? 'Avbryt' : 'Ny rolle'}
          </button>
        )}
      </div>

      {notice && (
        <p
          className="mt-3 text-[11px] text-rose px-3 py-2 rounded bg-rose/10 border border-rose/20"
          data-testid="sa-matrix-notice"
        >
          {notice}
        </p>
      )}

      {creating && result?.state === 'ok' && (
        <NewRoleForm
          organizations={organizations}
          linkDormant={linkDormant}
          onCreate={async (input) => {
            const res = await create(input);
            if (res.state === 'ok') {
              setCreating(false);
              await reload();
              return null;
            }
            if (res.state === 'refused') {
              return REFUSAL_TEXT[res.reason] ?? `Serveren avslo (${res.reason}).`;
            }
            return 'Kunne ikke opprette rollen.';
          }}
        />
      )}

      {!canWrite && result?.state === 'ok' && (
        <p className="mt-3 text-[11px] text-ink-3" data-testid="sa-matrix-readonly">
          Du har lesetilgang. Kun en global administrator kan endre matrisen.
        </p>
      )}

      <MatrixBody
        view={view}
        offerOverview={initialView === 'overview'}
        onView={setView}
        onSetCapabilities={(role, capabilities) =>
          setDrafts((d) => ({ ...d, [role.id]: capabilities }))
        }
        tab={tab}
        onTab={setTab}
        hidden={hidden}
        onHiddenToggle={(id) =>
          setHidden((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
          })
        }
        editing={editing}
        onEdit={(r) => setEditing({ id: r.id, name: r.name, description: r.description ?? '' })}
        onEditChange={(patch) => setEditing((e) => (e ? { ...e, ...patch } : e))}
        onEditCancel={() => setEditing(null)}
        onEditSave={() => void renameRole()}
        result={result}
        columns={columns}
        canWrite={canWrite}
        busyRole={busyRole}
        rowValue={rowValue}
        isDirty={isDirty}
        pendingChange={pendingChange}
        onToggle={toggle}
        onSave={(r) => void saveRow(r)}
        onRemove={(r) => void removeRow(r)}
        scopeRole={scopeRole}
        scopeNotice={scopeNotice}
        organizations={organizations}
        onScopeOpen={(id) => {
          setScopeNotice(null);
          setScopeRole((prev) => (prev === id ? null : id));
        }}
        onScopeSave={(role, input) => void saveAvailability(role, input)}
      />
    </section>
  );
}

function MatrixBody({
  tab,
  onTab,
  hidden,
  onHiddenToggle,
  result,
  columns,
  canWrite,
  busyRole,
  scopeRole,
  scopeNotice,
  organizations,
  onScopeOpen,
  onScopeSave,
  rowValue,
  isDirty,
  pendingChange,
  onToggle,
  onSave,
  onRemove,
  editing,
  onEdit,
  onEditChange,
  onEditCancel,
  onEditSave,
  view,
  offerOverview,
  onView,
  onSetCapabilities,
}: {
  /** The page opens on the one-page overview (owner decisions, 2026-10-19 and -20). */
  offerOverview: boolean;
  view: 'overview' | 'detail';
  onView: (v: 'overview' | 'detail') => void;
  onSetCapabilities: (r: RoleView, capabilities: string[]) => void;
  tab: Tab;
  onTab: (t: Tab) => void;
  result: MatrixResult | null;
  columns: CapabilityInfo[];
  canWrite: boolean;
  busyRole: string | null;
  rowValue: (r: RoleView) => string[];
  isDirty: (r: RoleView) => boolean;
  pendingChange: (r: RoleView) => { added: string[]; removed: string[] };
  onToggle: (r: RoleView, capability: string) => void;
  onSave: (r: RoleView) => void;
  onRemove: (r: RoleView) => void;
  hidden: Set<string>;
  onHiddenToggle: (id: string) => void;
  editing: { id: string; name: string; description: string } | null;
  onEdit: (r: RoleView) => void;
  onEditChange: (patch: Partial<{ name: string; description: string }>) => void;
  onEditCancel: () => void;
  onEditSave: () => void;
  scopeRole: string | null;
  scopeNotice: string | null;
  organizations: OrganizationSummary[];
  onScopeOpen: (id: string) => void;
  onScopeSave: (
    r: RoleView,
    input: { availability: 'PLATFORM' | 'SELECTED'; organizationIds: string[] },
  ) => void;
}) {
  const idBase = useId();
  const tabRefs = useRef<Partial<Record<Tab, HTMLButtonElement | null>>>({});
  if (!result) return null;
  if (result.state === 'no-api') {
    return (
      <p className="mt-3 text-[11px] text-ink-3" data-testid="sa-matrix-state">
        API-et er ikke koblet til i denne versjonen ennå.
      </p>
    );
  }
  if (result.state === 'forbidden') {
    return (
      <p className="mt-3 text-[11px] text-rose" data-testid="sa-matrix-state">
        Du har ikke tilgang til rettighetsmatrisen.
      </p>
    );
  }
  if (result.state === 'error') {
    return (
      <p className="mt-3 text-[11px] text-rose" data-testid="sa-matrix-state">
        Kunne ikke hente matrisen{result.status ? ` (HTTP ${result.status})` : ''}.
      </p>
    );
  }

  // ── ONE BOARD AT A TIME, AND ONE PER SCOPE BECAUSE THE SERVER ENFORCES ONE
  //
  // `validateCapabilitiesForRole` refuses an organization capability in a
  // PLATFORM role, a platform capability in an ORGANIZATION role, and
  // anything but the link class in a LINK role. A single grid of every role
  // against every capability is therefore mostly cells the server would
  // reject — a checkbox that cannot stick is worse than no checkbox, because
  // somebody has to tick it to find out.
  //
  // So each tab is a scope (the organization scope twice, split by side),
  // and every cell on it is a question the server will actually answer.
  // ── ONE PAGE, NO TABS (owner 1A/2A/3A, 2026-10-20) ─────────────────
  //
  // «Jeg liker ikke helt de knappene på toppen. Man mister litt
  // sammenhengen.» The overview is the page: every organization role in one
  // table grouped by who it is for, then Starpoint's roles, then
  // co-operation, stacked. Each role's full grid — every right, rename,
  // availability, delete — opens from its name, as the SAME `ScopeBoard`
  // restricted to that one role, so nothing the grid could do is lost. The
  // tabbed grid stays one link away at the bottom.
  if (view === 'overview') {
    const boardFor = (boardTab: Tab, boardRoles: RoleView[]) => (
      <ScopeBoard
        tab={boardTab}
        editing={editing}
        hidden={NOTHING_HIDDEN}
        onEdit={onEdit}
        onEditChange={onEditChange}
        onEditCancel={onEditCancel}
        onEditSave={onEditSave}
        roles={boardRoles}
        rows={columns.filter(
          (c) => c.derivation === SCOPE_DERIVATION[TABS.find((x) => x.tab === boardTab)!.scope],
        )}
        canWrite={canWrite}
        busyRole={busyRole}
        rowValue={rowValue}
        isDirty={isDirty}
        pendingChange={pendingChange}
        onToggle={onToggle}
        onSave={onSave}
        onRemove={onRemove}
        scopeRole={scopeRole}
        scopeNotice={scopeNotice}
        organizations={organizations}
        onScopeOpen={onScopeOpen}
        onScopeSave={onScopeSave}
      />
    );
    const organizationRoles = result.roles.filter((r) => r.scope === 'ORGANIZATION');
    const platformRoles = rolesForTab(result.roles, 'PLATFORM');
    const linkRoles = rolesForTab(result.roles, 'LINK');
    return (
      <div className="mt-4 flex flex-col gap-4" data-testid="sa-matrix-onepage">
        <AreaSection
          title="saAreas.section.organizations"
          lead="saAreas.section.organizations.lead"
          testId="sa-areas-section-organizations"
          empty={organizationRoles.length === 0}
        >
          <RoleAreaOverview
            roles={organizationRoles}
            rows={columns.filter((c) => c.derivation === 'organization')}
            titleOf={(c) => CAPABILITY_TITLE[c.key] ?? c.description ?? c.key}
            canWrite={canWrite}
            busyRole={busyRole}
            rowValue={rowValue}
            isDirty={isDirty}
            pendingChange={pendingChange}
            onSetCapabilities={onSetCapabilities}
            onToggle={onToggle}
            onSave={onSave}
            renderRoleDetail={(role) =>
              boardFor(role.appliesTo === 'CLUB' ? 'CLUB' : 'CENTRE', [role])
            }
          />
        </AreaSection>
        <AreaSection
          title="saAreas.section.platform"
          lead="saAreas.section.platform.lead"
          testId="sa-areas-section-platform"
          empty={platformRoles.length === 0}
        >
          {boardFor('PLATFORM', platformRoles)}
        </AreaSection>
        <AreaSection
          title="saAreas.section.link"
          lead="saAreas.section.link.lead"
          testId="sa-areas-section-link"
          empty={linkRoles.length === 0}
        >
          {boardFor('LINK', linkRoles)}
        </AreaSection>
        <div>
          <AreaViewLink to="detail" onView={onView} />
        </div>
      </div>
    );
  }

  const current = TABS.find((t) => t.tab === tab)!;
  const roles = rolesForTab(result.roles, tab);
  const rows = columns.filter((c) => c.derivation === SCOPE_DERIVATION[current.scope]);

  // THE LEGEND SPEAKS FOR THIS TAB (#861). Computed over every capability, it
  // stood above any board as long as one dormant right existed anywhere —
  // explaining a mark the tab on screen might not carry at all. `rows` is
  // the whole tab, the other side's folded rows included, since unfolding them
  // must not reveal a mark the legend never mentioned. A tab with no roles
  // draws no rows, so there is no mark for a legend to explain.
  const dormant = roles.length > 0 && rows.some((c) => c.inForce === false);

  // THE WAI-ARIA TABS PATTERN (#861): one tab stop for the whole list, the
  // arrow keys move between tabs (wrapping), Home and End jump to the ends,
  // and selection follows focus. One panel is drawn — the current tab's — so
  // every tab controls it and it is labelled by the selected one.
  const tabId = (t: Tab) => `${idBase}-tab-${t}`;
  const panelId = `${idBase}-panel`;
  const onTabKey = (e: KeyboardEvent<HTMLButtonElement>) => {
    const i = TABS.findIndex((t) => t.tab === tab);
    const next =
      e.key === 'ArrowRight'
        ? (i + 1) % TABS.length
        : e.key === 'ArrowLeft'
          ? (i - 1 + TABS.length) % TABS.length
          : e.key === 'Home'
            ? 0
            : e.key === 'End'
              ? TABS.length - 1
              : null;
    if (next === null) return;
    e.preventDefault();
    const target = TABS[next].tab;
    onTab(target);
    tabRefs.current[target]?.focus();
  };

  return (
    <div className="mt-4 flex flex-col gap-4" data-testid="sa-matrix-table">
      {/* Back to the one-page overview (2026-10-20). Only shown where the
          overview exists — the grid's own tests mount the grid alone. */}
      {offerOverview && (
        <div>
          <AreaViewLink to="overview" onView={onView} />
        </div>
      )}
      {/* THE TABS. A count on each, and a dot where a role has unsaved changes,
          so switching tabs never makes a draft silently disappear from view. */}
      <div role="tablist" aria-label="Hvem rollene er for" className="flex flex-wrap gap-1">
        {TABS.map((t) => {
          const tabRoles = rolesForTab(result.roles, t.tab);
          const dirty = tabRoles.some(isDirty);
          const active = t.tab === tab;
          return (
            <button
              key={t.tab}
              ref={(el) => {
                tabRefs.current[t.tab] = el;
              }}
              type="button"
              role="tab"
              id={tabId(t.tab)}
              aria-selected={active}
              aria-controls={panelId}
              tabIndex={active ? 0 : -1}
              onClick={() => onTab(t.tab)}
              onKeyDown={onTabKey}
              className={`inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors ${
                active
                  ? 'bg-ink text-white font-semibold'
                  : 'border border-line bg-paper text-ink-2 hover:bg-bg-soft'
              }`}
              data-testid={`sa-matrix-tab-${t.tab}`}
            >
              {t.label}
              <span
                className={`rounded-full px-1.5 text-[10px] ${
                  active ? 'bg-white/20 text-white' : 'bg-bg-soft text-ink-3'
                }`}
                title={`${tabRoles.length} roller`}
              >
                {tabRoles.length}
              </span>
              {dirty && (
                <span
                  className={active ? 'text-lime' : 'text-lime-deep'}
                  title="Ulagrede endringer"
                >
                  •
                </span>
              )}
            </button>
          );
        })}
      </div>

      {dormant && (
        /* THE LEGEND FOR THE DORMANT ROWS. Said once, above everything, so the
           mark on a row is a reminder and not a mystery. */
        <p
          className="text-[11px] text-ink-3 flex items-center gap-2"
          data-testid="sa-matrix-dormant-legend"
        >
          <DormantMark />
          <span>
            = ingen del av appen spør etter rettigheten ennå. Den kan tunes nå og slår inn når
            funksjonen bygges — ingen får eller mister noe før det.
          </span>
        </p>
      )}

      <section
        className="rounded-xl border border-line bg-paper overflow-hidden"
        role="tabpanel"
        id={panelId}
        aria-labelledby={tabId(tab)}
        data-testid="sa-matrix-panel"
      >
        {/* ONE TOOLBAR, NOT THREE ROWS. What the tab is, and which roles are
            shown, on one line — the grid starts right under it. */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 py-3 border-b border-line bg-bg-soft/60">
          <div className="mr-auto">
            <h3 className="font-display text-sm font-semibold text-ink">{current.label}</h3>
            <p className="text-[11px] text-ink-3">{current.blurb}</p>
          </div>
          {roles.length > 1 && (
            <RoleChooser
              roles={roles}
              hidden={hidden}
              isDirty={isDirty}
              onToggle={onHiddenToggle}
            />
          )}
        </div>
        {roles.length === 0 ? (
          <p className="px-4 py-4 text-xs text-ink-3" data-testid={`sa-matrix-no-roles-${tab}`}>
            Ingen roller her ennå. Lag en med «Ny rolle» og velg hvem den er for.
          </p>
        ) : rows.length === 0 ? (
          // Said out loud rather than drawn as an empty grid: «no
          // capabilities are defined for this yet» is information; a table
          // with no rows looks like a bug.
          <p
            className="px-4 py-3 text-[11px] text-ink-3"
            data-testid={`sa-matrix-empty-${current.scope}`}
          >
            Ingen rettigheter er definert for denne typen ennå.
          </p>
        ) : (
          <ScopeBoard
            tab={tab}
            editing={editing}
            hidden={hidden}
            onEdit={onEdit}
            onEditChange={onEditChange}
            onEditCancel={onEditCancel}
            onEditSave={onEditSave}
            roles={roles}
            rows={rows}
            canWrite={canWrite}
            busyRole={busyRole}
            rowValue={rowValue}
            isDirty={isDirty}
            pendingChange={pendingChange}
            onToggle={onToggle}
            onSave={onSave}
            onRemove={onRemove}
            scopeRole={scopeRole}
            scopeNotice={scopeNotice}
            organizations={organizations}
            onScopeOpen={onScopeOpen}
            onScopeSave={onScopeSave}
          />
        )}
      </section>
    </div>
  );
}

/**
 * ONE SCOPE'S GRID: capabilities down the side, roles across the top.
 *
 * TRANSPOSED FROM THE ORIGINAL, and the reason is that a row can carry a
 * sentence and a column head cannot. Deciding what a receptionist may do
 * means reading what each capability actually permits — and the pairs one
 * underscore apart (`rating.commit` / `rating.adjust_manual`) are exactly
 * where a 9px vertical column label costs somebody a wrong tick.
 *
 * It also fits: twenty-odd capabilities as rows against a handful of roles as
 * columns is a shape a screen can hold.
 */
/**
 * WHICH ROLES ARE COLUMNS.
 *
 * At five roles the grid fits; the day organizations own their own roles
 * (#535) it will not, and scrolling sideways past columns you do not care
 * about is how a tick lands in the wrong one. So the operator picks.
 *
 * COMPARISON IS THE POINT, which is why this hides columns rather than
 * showing one role at a time: you decide what a receptionist may do by
 * looking at what a manager already has. A single-role editor would be
 * tidier and would take that away.
 *
 * A ROLE WITH UNSAVED CHANGES CANNOT BE HIDDEN. Hiding it would take its Save
 * button off the screen while the edit stayed in memory — the change is not
 * lost, but nothing says it is there, and the next reload drops it silently.
 * Refusing is the honest answer, and the reason is on the control.
 */
function RoleChooser({
  roles,
  hidden,
  isDirty,
  onToggle,
}: {
  roles: RoleView[];
  hidden: Set<string>;
  isDirty: (r: RoleView) => boolean;
  onToggle: (id: string) => void;
}) {
  return (
    <div className="flex flex-wrap items-center gap-1.5" data-testid="sa-matrix-role-chooser">
      <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3 mr-1">Vis</span>
      {roles.map((role) => {
        const shown = !hidden.has(role.id);
        const dirty = isDirty(role);
        return (
          <button
            key={role.id}
            type="button"
            // Only a SHOWN role with changes is locked: hiding is what would
            // strand the edit, and re-showing one can never do harm.
            disabled={dirty && shown}
            title={
              dirty && shown
                ? 'Rollen har ulagrede endringer — lagre eller avbryt før du skjuler den.'
                : shown
                  ? 'Skjul kolonnen'
                  : 'Vis kolonnen'
            }
            onClick={() => onToggle(role.id)}
            className={`rounded-full border px-2.5 py-0.5 text-[11px] transition-colors ${
              shown
                ? 'border-lime-deep bg-lime-bg text-ink font-medium'
                : 'border-line text-ink-3 hover:bg-bg-soft line-through'
            }`}
            aria-pressed={shown}
            data-testid={`sa-matrix-show-${role.key}`}
          >
            {role.name}
            {dirty && ' •'}
          </button>
        );
      })}
    </div>
  );
}

/** The small mark that says «no route asks for this yet». One shape, used in the
 *  legend and on the rows, so the two cannot drift apart — and on the
 *  organizations page's centre–club links (#861), which nothing reads yet
 *  either, with a `title` saying so about a link rather than a right. */
export function DormantMark({ testId, title }: { testId?: string; title?: string }) {
  return (
    <span
      className="inline-flex items-center gap-1 rounded-full border border-amber-300 bg-amber-50 px-1.5 py-px text-[10px] text-amber-900 whitespace-nowrap"
      title={
        title ??
        'Ingen del av appen spør etter denne rettigheten ennå. En avkrysning her slår inn den dagen funksjonen bygges.'
      }
      data-testid={testId}
    >
      <span className="h-1.5 w-1.5 rounded-full bg-amber-400" aria-hidden="true" />
      ikke i kraft
    </span>
  );
}

function ScopeBoard({
  tab,
  roles: allRoles,
  hidden,
  rows,
  canWrite,
  busyRole,
  rowValue,
  isDirty,
  pendingChange,
  onToggle,
  onSave,
  onRemove,
  editing,
  onEdit,
  onEditChange,
  onEditCancel,
  onEditSave,
  scopeRole,
  scopeNotice,
  organizations,
  onScopeOpen,
  onScopeSave,
}: {
  tab: Tab;
  roles: RoleView[];
  rows: CapabilityInfo[];
  canWrite: boolean;
  busyRole: string | null;
  rowValue: (r: RoleView) => string[];
  isDirty: (r: RoleView) => boolean;
  pendingChange: (r: RoleView) => { added: string[]; removed: string[] };
  onToggle: (r: RoleView, capability: string) => void;
  onSave: (r: RoleView) => void;
  onRemove: (r: RoleView) => void;
  hidden: Set<string>;
  editing: { id: string; name: string; description: string } | null;
  onEdit: (r: RoleView) => void;
  onEditChange: (patch: Partial<{ name: string; description: string }>) => void;
  onEditCancel: () => void;
  onEditSave: () => void;
  scopeRole: string | null;
  scopeNotice: string | null;
  organizations: OrganizationSummary[];
  onScopeOpen: (id: string) => void;
  onScopeSave: (
    r: RoleView,
    input: { availability: 'PLATFORM' | 'SELECTED'; organizationIds: string[] },
  ) => void;
}) {
  // WHAT THE CHOOSER LEFT. Derived rather than stored, so hiding a role
  // changes what is drawn and nothing else — the draft, the dirty state and
  // the pending summary all survive being hidden and come back with it.
  const roles = allRoles.filter((r) => !hidden.has(r.id));

  // THIS SIDE'S RIGHTS, AND THE OTHER SIDE'S. On the Senter tab the club's
  // categories (matches, results, rating) are folded at the bottom rather
  // than removed: a shared role may still hold them, and a save sends the
  // whole row, so what is folded is kept exactly as it was.
  const otherSide: Side | null = tab === 'CENTRE' ? 'CLUB' : tab === 'CLUB' ? 'CENTRE' : null;
  const own = rows.filter((c) => otherSide === null || sideOf(c) !== otherSide);
  const other = rows.filter((c) => otherSide !== null && sideOf(c) === otherSide);
  const [otherOpen, setOtherOpen] = useState(false);

  // Grouped by the capability's domain, merged across the list and drawn in
  // the product's order — so «Baner og anlegg» is one group, not two.
  const domains = groupByDomain(own);
  const otherDomains = groupByDomain(other);
  const colSpan = roles.length + 1;

  // WHERE THE SHARED MARK GOES. When every role on the tab is for both sides
  // — today's six system roles — one sentence in the toolbar says so; a pill
  // under each of six columns said the same thing six times and read as
  // noise. The per-column mark appears once the tab mixes shared and own.
  const shared = otherSide !== null && roles.length > 0 && roles.every((r) => r.appliesTo == null);
  const mixed = otherSide !== null && !shared;

  /** One category: its coloured heading, then one row per right. */
  const renderCategory = ({ domain, caps }: { domain: string; caps: CapabilityInfo[] }) => {
    const cat = categoryOf(domain);
    // The side tag is only meaningful where the board is split by side.
    const side = otherSide === null ? null : sideOf(caps[0]);
    return (
      <Fragment key={domain}>
        <tr data-testid={`sa-matrix-category-${domain}`}>
          {/* The CONTENT is sticky, not the cell (#861): a cell as wide as the
              whole table has nowhere to stick to, so once the board scrolls
              sideways its heading scrolled out with it and left a blank band. */}
          <td colSpan={colSpan} className={`px-4 pt-3 pb-2 border-y border-line ${cat.tone.head}`}>
            <div className="sticky left-4 flex w-fit max-w-full items-center gap-3">
              <span
                className={`inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg ${cat.tone.icon}`}
                aria-hidden="true"
              >
                <cat.Icon size={15} />
              </span>
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-display text-sm font-semibold">{cat.label}</span>
                  <span className="rounded-full bg-white/70 px-1.5 text-[10px] text-ink-3">
                    {caps.length}
                  </span>
                  {side && (
                    <span
                      className={`rounded-full px-2 py-px text-[10px] font-medium ${
                        side === 'BOTH' ? 'bg-white/70 text-ink-3' : 'bg-ink text-white'
                      }`}
                      title={
                        side === 'BOTH'
                          ? 'Rettigheter både et senter og en klubb har bruk for.'
                          : `${SIDE_LABEL[side]} område.`
                      }
                    >
                      {SIDE_LABEL[side]}
                    </span>
                  )}
                </div>
                {cat.blurb && <div className="text-[11px] opacity-70 mt-0.5">{cat.blurb}</div>}
              </div>
            </div>
          </td>
        </tr>
        {caps.map((c) => {
          const title = CAPABILITY_TITLE[c.key];
          const sentence = c.description ?? c.key;
          return (
            <tr key={c.key} className="border-b border-line-soft align-middle hover:bg-lime-bg/40">
              <td
                className={`sticky left-0 z-10 bg-paper pl-3 pr-3 py-2 border-l-4 ${cat.tone.stripe}`}
              >
                {/* A SHORT NAME TO SCAN, THE SENTENCE TO READ. The identifier
                    is what the API, the audit trail and every error message
                    name, so it is one hover away rather than printed. */}
                {/* Stacked on a phone, side by side from `sm` (#861): in a row
                    the «ikke i kraft» pill adds its own width to the column's
                    narrowest size, and that is the width the pinned column
                    covers the role checkboxes with. */}
                <div className="flex flex-col items-start gap-1 sm:flex-row sm:justify-between sm:gap-3">
                  <div className="min-w-0" title={c.key}>
                    <div className="text-xs font-medium text-ink leading-snug">
                      {title ?? sentence}
                    </div>
                    {title && (
                      <div className="text-[11px] text-ink-3 leading-snug mt-0.5">{sentence}</div>
                    )}
                  </div>
                  {c.inForce === false && (
                    <span className="shrink-0 pt-px">
                      <DormantMark testId={`sa-matrix-dormant-${c.key}`} />
                    </span>
                  )}
                </div>
              </td>
              {roles.map((role) => (
                <td key={role.id} className="text-center px-3 py-2">
                  <input
                    type="checkbox"
                    className="h-4 w-4 cursor-pointer disabled:cursor-default"
                    style={{ accentColor: 'var(--lime-deep)' }}
                    checked={rowValue(role).includes(c.key)}
                    disabled={!canWrite || busyRole === role.id}
                    onChange={() => onToggle(role, c.key)}
                    aria-label={`${role.key}: ${c.key}`}
                    data-testid={`sa-matrix-cell-${role.key}-${c.key}`}
                  />
                </td>
              ))}
            </tr>
          );
        })}
      </Fragment>
    );
  };

  return (
    /* THE PAGE SCROLLS DOWN, THE BOARD SCROLLS SIDEWAYS. An inner scroll
       box of 70vh cut the organization board off mid-way and hid half the
       rights behind a second scrollbar nobody expected, so there is no
       vertical box here.

       Sideways it scrolls at EVERY width (#861). It used to turn
       `md:overflow-visible` so the role names could stick to the viewport,
       which never worked: a sticky cell sticks to its nearest scroll box, and
       the card around it is `overflow-hidden` (for its rounded corners). What
       it did do was leave a board wider than the card — the centre tab has
       twelve role columns — with no scroll of its own: cut off by the card
       wherever the column held its width, and in `/sa`'s grid widening the
       whole page past `max-w-6xl` instead. Here the «Rettighet» column stays
       pinned while the roles scroll past it. It also needs the grid column to
       be allowed to shrink (`min-w-0` on `<main>` in `SuperadminRoute`), or
       the board widens the page instead of scrolling. Pinned by
       `permission-matrix-section.test.tsx` and `superadmin-route.test.tsx`. */
    <div className="overflow-x-auto" data-testid="sa-matrix-scroll">
      {shared && (
        <p
          className="sticky left-0 px-4 py-2 text-[11px] text-ink-3 border-b border-line-soft"
          data-testid="sa-matrix-all-shared"
        >
          Alle rollene her er felles for sentre og klubber — en endring gjelder begge.
        </p>
      )}
      <table className="min-w-full text-[11px] border-collapse">
        <thead>
          <tr className="border-b border-line">
            {/* THE PINNED COLUMN IS AT MOST 40% OF THE SCREEN (#861). It was
                `min-w-[340px]` — wider than a phone's whole board, so the
                pinned column covered every role checkbox and there was
                nothing left to scroll into view. Once the roles overflow, the
                table sits at its narrowest and this column at its minimum,
                so the minimum is what decides how much of the board stays
                usable: 340px from a ~850px screen up, 40vw below that. */}
            <th className="sticky left-0 top-0 z-20 bg-paper text-left font-mono text-[10px] uppercase tracking-wider text-ink-3 pl-4 pr-3 py-2 align-bottom min-w-[min(340px,40vw)] w-[46%]">
              Rettighet
            </th>
            {roles.map((role) => (
              <th
                key={role.id}
                className="sticky top-0 z-10 bg-paper px-3 py-2 align-bottom text-center whitespace-nowrap"
                /* THE KEY IS NOT ON SCREEN, BUT IT IS NOT GONE (#535 review).
                   The key is generated, never chosen, and not editable here; it
                   stays as the column's tooltip because it is what the audit
                   trail, the API and every error message name. So does whether
                   the role is a system role — a label under every column said
                   «system» five times and told nobody anything. */
                title={`${role.key} · ${
                  role.ownerName ? role.ownerName : role.isSystem ? 'systemrolle' : 'egendefinert'
                }`}
              >
                <div className="text-xs font-semibold text-ink">{role.name}</div>
                <div className="mt-0.5 flex items-center justify-center gap-1">
                  <span
                    className="inline-block rounded-full bg-bg-soft px-1.5 text-[10px] text-ink-3 normal-case"
                    title={`${role.holders} ${role.holders === 1 ? 'person har' : 'personer har'} rollen`}
                  >
                    {role.holders}
                  </span>
                  {/* A ROLE FOR BOTH SIDES SAYS SO. The tab is «Senter», but a
                      tick on this column reaches every club that has the role
                      too — the one thing the split must not hide. */}
                  {mixed && role.appliesTo == null && (
                    <span
                      className="inline-block rounded-full border border-line px-1.5 text-[10px] font-normal text-ink-3 normal-case"
                      title="Rollen finnes hos både sentre og klubber. En endring gjelder begge."
                      data-testid={`sa-matrix-shared-${role.key}`}
                    >
                      senter + klubb
                    </span>
                  )}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {domains.map(renderCategory)}
          {other.length > 0 && otherSide && (
            /* THE OTHER SIDE, FOLDED. Named, counted and one click away — not
               removed, because a shared role may hold these and somebody has
               to be able to see and change that from either tab. */
            <tr>
              <td colSpan={colSpan} className="bg-bg-soft/50 px-4 py-2.5">
                <button
                  type="button"
                  className="sticky left-4 flex w-fit items-center gap-2 text-xs text-ink-2 hover:text-ink"
                  onClick={() => setOtherOpen((v) => !v)}
                  aria-expanded={otherOpen}
                  data-testid="sa-matrix-other-side"
                >
                  {otherOpen ? (
                    <ChevronDown size={14} aria-hidden="true" />
                  ) : (
                    <ChevronRight size={14} aria-hidden="true" />
                  )}
                  <span className="font-medium">{SIDE_LABEL[otherSide]} område</span>
                  <span className="text-ink-3">
                    {otherDomains.map((d) => categoryOf(d.domain).label).join(', ')} ·{' '}
                    {other.length} {other.length === 1 ? 'rettighet' : 'rettigheter'}
                  </span>
                </button>
                <span className="ml-2 text-[11px] text-ink-3">
                  Hører til {otherSide === 'CLUB' ? 'klubben' : 'senteret'}, men en felles rolle kan
                  holde dem.
                </span>
              </td>
            </tr>
          )}
          {otherOpen && otherDomains.map(renderCategory)}
        </tbody>
        {canWrite && (
          <tfoot>
            <tr className="border-t border-line bg-bg-soft/40">
              <td className="sticky left-0 bg-bg-soft/40 pl-4 pt-3 pb-3 text-[11px] text-ink-3 align-top">
                Lagre endringer
              </td>
              {roles.map((role) => {
                const dirty = isDirty(role);
                const busy = busyRole === role.id;
                const { added, removed } = pendingChange(role);
                return (
                  <td key={role.id} className="pt-3 pb-3 px-2 align-top">
                    {/* ONE COLUMN, STACKED. Side by side, three small buttons
                        wrapped unpredictably under a short role name and the
                        column read as a jumble. Save is the one that matters
                        and sits first; the rest are quiet text links under it. */}
                    <div className="flex flex-col items-center gap-1.5">
                      <button
                        type="button"
                        className={`${dirty ? 'btn-lime' : 'btn-outline'} text-[11px] px-3 py-1`}
                        disabled={!dirty || busy}
                        onClick={() => onSave(role)}
                        data-testid={`sa-matrix-save-${role.key}`}
                      >
                        {busy ? 'Lagrer…' : 'Lagre'}
                      </button>
                      {/* WHAT THE SAVE WILL DO, before it does it. A mis-tick in
                          an authorization matrix is silent: the page looks the
                          same either way, and the difference only shows up when
                          somebody cannot do their job. */}
                      {dirty && (
                        <div
                          className="text-[10px] text-ink-2 text-center leading-tight"
                          data-testid={`sa-matrix-pending-${role.key}`}
                        >
                          {added.length > 0 && <div>+{added.length} gis</div>}
                          {removed.length > 0 && <div>−{removed.length} fjernes</div>}
                        </div>
                      )}
                      <div className="flex flex-wrap items-center justify-center gap-x-2 gap-y-0.5 text-[10px] whitespace-nowrap">
                        {/* RENAME IS OFFERED FOR SYSTEM ROLES TOO. The server
                            refuses to change a role's KEY — that is the identifier
                            the authorization service names — but the name and the
                            description are the human half, and «TENANT_STAFF»
                            needs a better one on screen than in code. */}
                        <button
                          type="button"
                          className="text-ink-3 hover:text-ink underline-offset-2 hover:underline disabled:opacity-40"
                          disabled={busy}
                          onClick={() => onEdit(role)}
                          data-testid={`sa-matrix-rename-${role.key}`}
                        >
                          Navn
                        </button>
                        {/* AVAILABILITY IS OFFERED ONLY WHERE IT HAS A MEANING.
                            A system role is available everywhere by design, and a
                            PLATFORM or LINK role has no organization to be
                            restricted to — the server refuses both, so the control
                            is absent rather than present-and-failing. */}
                        {!role.isSystem && role.scope === 'ORGANIZATION' && (
                          <button
                            type="button"
                            className="text-ink-3 hover:text-ink underline-offset-2 hover:underline disabled:opacity-40"
                            disabled={busy}
                            onClick={() => onScopeOpen(role.id)}
                            aria-pressed={scopeRole === role.id}
                            data-testid={`sa-matrix-scope-${role.key}`}
                          >
                            {/* The action, with the state beside it. */}
                            Tilgjengelig for…{' '}
                            <span className="text-ink-4">
                              (
                              {role.availability === 'SELECTED'
                                ? `${role.organizations.length} valgt`
                                : 'alle'}
                              )
                            </span>
                          </button>
                        )}
                        {!role.isSystem && role.holders === 0 && (
                          <button
                            type="button"
                            className="text-ink-3 hover:text-rose underline-offset-2 hover:underline disabled:opacity-40"
                            disabled={busy}
                            onClick={() => onRemove(role)}
                            data-testid={`sa-matrix-delete-${role.key}`}
                          >
                            Slett
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                );
              })}
            </tr>
          </tfoot>
        )}
      </table>

      {/* BELOW THE TABLE, NOT IN THE CELL. A role column is as wide as its
          name; two text inputs do not fit in one and would be unusable at
          that width. It appears only for the role being edited, so there is
          never a question about which role a field belongs to. */}
      {editing && roles.some((r) => r.id === editing.id) && (
        <form
          className="mt-3 flex flex-wrap items-end gap-2 border-t border-white/10 pt-3"
          data-testid="sa-matrix-rename-form"
          onSubmit={(e) => {
            e.preventDefault();
            onEditSave();
          }}
        >
          <label className="flex flex-col">
            <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">Navn</span>
            <input
              className="input text-xs"
              value={editing.name}
              onChange={(e) => onEditChange({ name: e.target.value })}
              data-testid="sa-matrix-rename-name"
            />
          </label>
          <label className="flex flex-col grow">
            <span className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
              Beskrivelse
            </span>
            <input
              className="input text-xs w-full"
              value={editing.description}
              onChange={(e) => onEditChange({ description: e.target.value })}
              placeholder="Hva rollen er til for"
              data-testid="sa-matrix-rename-description"
            />
          </label>
          <button
            type="submit"
            className="btn-lime text-[11px]"
            disabled={!editing.name.trim()}
            data-testid="sa-matrix-rename-save"
          >
            Lagre navn
          </button>
          <button
            type="button"
            className="btn-ghost text-[11px]"
            onClick={onEditCancel}
            data-testid="sa-matrix-rename-cancel"
          >
            Avbryt
          </button>
        </form>
      )}

      {/* BELOW THE TABLE for the same reason the rename form is: a role column
          is as wide as its name, and a search field with a scrolling list does
          not fit in one. Keyed on the role so switching roles resets the
          picker's staging rather than carrying a half-made selection across. */}
      {(() => {
        const role = roles.find((r) => r.id === scopeRole);
        if (!role) return null;
        return (
          <OrganizationScopePicker
            key={role.id}
            role={role}
            organizations={organizations}
            busy={busyRole === role.id}
            notice={scopeNotice}
            onSave={(input) => onScopeSave(role, input)}
          />
        );
      })()}
    </div>
  );
}

/**
 * Creating a role: a NAME, and who it is for.
 *
 * THE KEY IS GONE FROM THIS FORM (#535 follow-up). It asked an operator to
 * invent an identifier in a format they had no reason to know, for a value
 * they never see again — the matrix shows the name. The server derives it
 * («Senter resepsjon» → `SENTER_RESEPSJON`) and suffixes a collision, so two
 * clubs may both have a «Resepsjon».
 *
 * AND THE TWO SELECTS BECAME QUESTIONS. «Gjelder: Organisasjon» and «Type
 * organisasjon: Begge» are the model's words, not a reader's; each choice now
 * says what it means for the person holding the role. The common case —
 * somebody who works at a club — is the default, so the ordinary path is to
 * type a name and press the button.
 */
function NewRoleForm({
  onCreate,
  organizations,
  linkDormant,
}: {
  organizations: OrganizationSummary[];
  /** True while no link right is in force — what the LINK choice's help says. */
  linkDormant: boolean;
  onCreate: (input: {
    name: string;
    description?: string;
    scope: 'PLATFORM' | 'ORGANIZATION' | 'LINK';
    appliesTo?: 'CENTRE' | 'CLUB' | null;
    availability?: 'PLATFORM' | 'SELECTED';
    organizationIds?: string[];
  }) => Promise<string | null>;
}) {
  const [name, setName] = useState('');
  const [scope, setScope] = useState<'PLATFORM' | 'ORGANIZATION' | 'LINK'>('ORGANIZATION');
  const [appliesTo, setAppliesTo] = useState<'' | 'CENTRE' | 'CLUB'>('');
  const [availability, setAvailability] = useState<'PLATFORM' | 'SELECTED'>('PLATFORM');
  const [chosen, setChosen] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const eligible = organizations.filter((o) => !appliesTo || o.kind === appliesTo);

  return (
    <form
      className="mt-4 border-t border-white/10 pt-4"
      data-testid="sa-matrix-new-form"
      onSubmit={async (e) => {
        e.preventDefault();
        setBusy(true);
        setError(null);
        try {
          const organization = scope === 'ORGANIZATION';
          const err = await onCreate({
            name: name.trim(),
            scope,
            appliesTo: organization && appliesTo ? appliesTo : null,
            // Only meaningful for an organization role; the server refuses
            // `SELECTED` on the others, so the page does not offer it.
            availability: organization ? availability : 'PLATFORM',
            organizationIds: organization && availability === 'SELECTED' ? chosen : [],
          });
          if (err) setError(err);
          else {
            setName('');
            setChosen([]);
            setAvailability('PLATFORM');
          }
        } finally {
          setBusy(false);
        }
      }}
    >
      <label className="block text-[11px] text-ink-3">
        Navn på rollen
        <input
          className="input w-full max-w-xs text-xs mt-1"
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Resepsjon"
          data-testid="sa-matrix-new-name"
        />
      </label>

      <fieldset className="mt-3">
        <legend className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
          Hvem skal ha rollen?
        </legend>
        <div className="mt-1.5 space-y-1.5">
          {(
            [
              [
                'ORGANIZATION',
                'Noen som jobber i en klubb eller et senter',
                'Det vanlige. Rollen gis til folk i organisasjonen, og gjelder bare der.',
              ],
              [
                'PLATFORM',
                'Noen hos Starpoint',
                'Gir tilgang i dette panelet, på tvers av alle klubber. Ikke i klubbportalen.',
              ],
              [
                'LINK',
                'Et tilgangsnivå et senter gir en klubb',
                // «Kan ikke ha rettigheter ennå» was false (#861): a LINK role
                // holds the link rights, ticked on «Samarbeid». What is not
                // there yet is a route that reads them.
                linkDormant
                  ? 'Henger på koblingen mellom et senter og en klubb. Rettighetene krysses av under «Samarbeid», men ingen del av appen leser dem ennå.'
                  : 'Henger på koblingen mellom et senter og en klubb. Rettighetene krysses av under «Samarbeid».',
              ],
            ] as const
          ).map(([value, label, help]) => (
            <label key={value} className="flex gap-2 text-xs">
              <input
                type="radio"
                name="new-role-scope"
                className="mt-0.5"
                checked={scope === value}
                onChange={() => setScope(value)}
                data-testid={`sa-matrix-new-scope-${value}`}
              />
              <span>
                {label}
                <span className="block text-[11px] text-ink-3">{help}</span>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      {scope === 'ORGANIZATION' && (
        <>
          <label className="mt-3 block text-[11px] text-ink-3">
            Kan brukes i
            <select
              className="input text-xs mt-1 block"
              value={appliesTo}
              onChange={(e) => {
                setAppliesTo(e.target.value as typeof appliesTo);
                // A club-only role cannot be offered to a centre, so a change
                // here drops any choice the new rule would refuse.
                setChosen([]);
              }}
              data-testid="sa-matrix-new-applies"
            >
              <option value="">Både sentre og klubber</option>
              <option value="CENTRE">Bare sentre</option>
              <option value="CLUB">Bare klubber</option>
            </select>
          </label>

          <fieldset className="mt-3">
            <legend className="font-mono text-[10px] uppercase tracking-wider text-ink-3">
              Tilgjengelig for
            </legend>
            <div className="mt-1.5 flex flex-wrap gap-4">
              {(
                [
                  ['PLATFORM', 'Alle organisasjoner'],
                  ['SELECTED', 'Bare noen jeg velger'],
                ] as const
              ).map(([value, label]) => (
                <label key={value} className="flex items-center gap-1.5 text-xs">
                  <input
                    type="radio"
                    name="new-role-availability"
                    checked={availability === value}
                    onChange={() => setAvailability(value)}
                    data-testid={`sa-matrix-new-availability-${value.toLowerCase()}`}
                  />
                  {label}
                </label>
              ))}
            </div>

            {/* THE PICKER, HERE (#535 follow-up). It existed only on an
                already-created role, behind a small button in the table
                footer — so the one moment somebody is deciding a role's reach
                was the one moment it was not on screen. */}
            {availability === 'SELECTED' && (
              <ul
                className="mt-2 max-h-40 overflow-y-auto rounded border border-white/10"
                data-testid="sa-matrix-new-orgs"
              >
                {eligible.length === 0 && (
                  <li className="px-2 py-1.5 text-[11px] text-ink-3">
                    Ingen organisasjoner av denne typen.
                  </li>
                )}
                {eligible.map((o) => {
                  const on = chosen.includes(o.id);
                  return (
                    <li key={o.id}>
                      <label className="flex items-center gap-2 px-2 py-1.5 text-xs hover:bg-white/5">
                        <input
                          type="checkbox"
                          checked={on}
                          onChange={() =>
                            setChosen((prev) =>
                              on ? prev.filter((x) => x !== o.id) : [...prev, o.id],
                            )
                          }
                          data-testid={`sa-matrix-new-org-${o.id}`}
                        />
                        {o.name}
                        <span className="ml-auto text-[10px] text-ink-3">
                          {o.kind === 'CENTRE' ? 'senter' : 'klubb'}
                        </span>
                      </label>
                    </li>
                  );
                })}
              </ul>
            )}
            {availability === 'SELECTED' && chosen.length === 0 && (
              <p className="mt-1 text-[11px] text-orange" data-testid="sa-matrix-new-orgs-none">
                Ingen valgt — rollen kan ikke tildeles noe sted før du velger minst én.
              </p>
            )}
          </fieldset>
        </>
      )}

      <button
        type="submit"
        className="btn-lime text-xs mt-3"
        disabled={busy || !name.trim()}
        data-testid="sa-matrix-new-submit"
      >
        {busy ? 'Oppretter…' : 'Opprett rollen'}
      </button>
      {/* Said out loud, because the key used to be a field somebody filled in
          and its disappearance should not look like a loss. */}
      <p className="mt-1 text-[11px] text-ink-3">
        Rollen får en teknisk nøkkel automatisk, laget av navnet.
      </p>
      {error && (
        <p className="mt-2 text-[11px] text-rose" data-testid="sa-matrix-new-error">
          {error}
        </p>
      )}
    </form>
  );
}
